import { Collapse } from 'src/design/atoms/Collapse';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { Input } from 'src/design/atoms/Input';
import { useDisclosure } from '@mantine/hooks';
import React from 'react';
import ReactQuill from 'react-quill';
import { MeetingTopicDto, TopicIssueDto } from 'src/api/FiretrolApiClient';
import {
  useResolveAttachmentUrl,
  useUploadTempAttachment,
} from 'src/api/queries/attachments/useAttachmentMutations';
import {
  useCreateMeetingIssue,
  useDeleteMeetingIssue,
} from 'src/api/queries/meetings/useMeetingIssueMutations';
import {
  useDeleteMeetingTopic,
  useUpdateMeetingTopic,
} from 'src/api/queries/meetings/useMeetingTopicMutations';
import { FileDrop } from 'src/design/organisms/FileDrop/FileDrop';
import { applyAttachmentChange } from 'src/design/organisms/attachments/attachmentEdits';
import useDebounce from 'src/hooks/useDebounce';
import { usePendingEdit } from 'src/routes/Jobs/MeetingMinutes/MeetingForm/pendingEdits';
import { CreateActionIcon } from 'src/design/atoms/CreateActionIcon';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { InlineTextInput } from 'src/design/molecules/InlineTextInput';
import { CommentDrawer } from './components/CommentDrawer';
import { TopicIssue } from './components/TopicIssue';

interface ITopicProps {
  topic: MeetingTopicDto;
  onTopicDeleted: (topic: MeetingTopicDto) => void;
  onTopicChange: (topic: MeetingTopicDto) => void;
}

const autosaveSignature = (topic: MeetingTopicDto) =>
  JSON.stringify([
    topic.title,
    topic.notepad,
    topic.attachmentsToAdd,
    topic.attachmentsToRemove,
  ]);

export const Topic: React.FC<ITopicProps> = ({
  topic,
  onTopicChange,
  onTopicDeleted,
}) => {
  const jobId = topic.jobId!;
  const meetingId = topic.meetingId!;

  const [selectedIssueId, setSelectedIssueId] = React.useState<string | null>(
    null,
  );
  const [openedNotepad, { toggle: toggleNotepad }] = useDisclosure(false);

  // Typed text outranks the prop only until the accepted tree comes back
  // holding it. Dropping the override then — rather than leaving it to be
  // compared forever — is what lets the next notepad saved elsewhere reach this
  // editor: a kept override would differ from that value and look like fresh
  // typing, and the debounce would write the old text back over it.
  const [typedNotepad, setTypedNotepad] = React.useState<string | null>(null);
  if (typedNotepad !== null && typedNotepad === topic.notepad) {
    setTypedNotepad(null);
  }
  const hasUnsavedNotepad =
    typedNotepad !== null && typedNotepad !== topic.notepad;
  const notepad = hasUnsavedNotepad ? typedNotepad : topic.notepad;

  const debouncedNotepad = useDebounce(notepad, 1000);
  usePendingEdit(`topic-notepad-${topic.id}`, hasUnsavedNotepad);

  const updateTopic = useUpdateMeetingTopic(jobId, meetingId, topic.id);
  const deleteTopic = useDeleteMeetingTopic(jobId, meetingId);
  const createIssue = useCreateMeetingIssue(jobId, meetingId);
  const deleteIssue = useDeleteMeetingIssue(jobId, meetingId);
  const uploadAttachment = useUploadTempAttachment('meeting-topics');
  const resolveAttachmentUrl = useResolveAttachmentUrl();

  const selectedIssue =
    (topic.issues ?? []).find((issue) => issue.id === selectedIssueId) ?? null;

  // The tree in `topic` can lag the editor by a debounce, so every change built
  // here carries the notepad on screen rather than the one in the prop.
  const applyTopicChange = (next: MeetingTopicDto) =>
    onTopicChange({ ...next, notepad });

  // The autosave fires from an effect, so the last payload the server accepted
  // is remembered: without it the mutation's own invalidation would feed a new
  // topic object back in and re-trigger the save forever. A failed write leaves
  // the mark behind, so the next edit resends the text instead of losing it.
  const { mutate: saveTopic } = updateTopic;
  const lastSaved = React.useRef(autosaveSignature(topic));
  const inFlight = React.useRef<string | null>(null);

  React.useEffect(() => {
    const signature = autosaveSignature(topic);
    if (signature === lastSaved.current) return;
    if (signature === inFlight.current) return;
    inFlight.current = signature;
    saveTopic(topic, {
      onSuccess: () => {
        lastSaved.current = signature;
      },
      onSettled: () => {
        if (inFlight.current === signature) inFlight.current = null;
      },
    });
  }, [topic, saveTopic]);

  React.useEffect(() => {
    if (debouncedNotepad === topic.notepad) return;
    onTopicChange({ ...topic, notepad: debouncedNotepad });
  }, [debouncedNotepad]);

  const addIssue = () => createIssue.mutate(topic.id);

  const onIssueChange = (issue: TopicIssueDto) => {
    applyTopicChange({
      ...topic,
      issues: (topic.issues ?? []).map((is) =>
        is.id === issue.id ? { ...issue, comments: is.comments } : is,
      ),
    });
  };

  const removeIssue = (target: TopicIssueDto) => {
    setSelectedIssueId(null);
    deleteIssue.mutate({ topicId: target.topicId!, issueId: target.id });
    applyTopicChange({
      ...topic,
      issues: (topic.issues ?? []).filter((issue) => issue.id !== target.id),
    });
  };

  return (
    <Paper pos="relative">
      <LoadingOverlay
        visible={createIssue.isPending || deleteTopic.isPending}
      />

      <Stack gap="lg">
        <Group justify="space-between">
          <InlineTextInput
            initialValue={topic.title}
            onSave={(val) => applyTopicChange({ ...topic, title: val })}
            textProps={{ size: 'lg', c: 'black', fw: 700 }}
          />
          <DeleteActionIcon
            requiredConfirmation={true}
            confirmationTitle="Delete Topic"
            confirmationText="Are you sure you want to remove this topic? This action cannot be reversed."
            onDelete={() =>
              deleteTopic.mutate(topic.id, {
                onSuccess: () => onTopicDeleted(topic),
              })
            }
            loading={deleteTopic.isPending}
          />
        </Group>

        <Group>
          <Title order={5}>Issues</Title>
          <CreateActionIcon
            loading={createIssue.isPending}
            onClick={addIssue}
          />
        </Group>

        {(topic.issues ?? []).map((issue) => (
          <TopicIssue
            key={issue.id}
            issue={issue}
            jobId={jobId}
            meetingId={meetingId}
            onAddIssue={addIssue}
            onIssueChange={onIssueChange}
            onAddCommentClick={() => setSelectedIssueId(issue.id)}
            onDeleteIssue={() => removeIssue(issue)}
            selected={selectedIssueId === issue.id}
          />
        ))}

        <Group>
          <Title order={5}>Notepad</Title>
          <EditActionIcon
            onClick={toggleNotepad}
            variant={openedNotepad ? 'filled' : 'light'}
          />
        </Group>

        <Collapse in={openedNotepad}>
          <ReactQuill
            theme="snow"
            value={notepad}
            onChange={(value) => {
              setTypedNotepad(value);
            }}
          />
        </Collapse>

        <Input.Wrapper
          label={<Title order={5}>Attachments</Title>}
          description="Upload files related to this topic"
        >
          <FileDrop
            files={topic.attachments ?? []}
            onChange={(files, change) => {
              const edits = applyAttachmentChange(
                {
                  added: topic.attachmentsToAdd ?? [],
                  removed: topic.attachmentsToRemove ?? [],
                },
                change,
              );
              applyTopicChange({
                ...topic,
                attachments: files,
                attachmentsToAdd: edits.added,
                attachmentsToRemove: edits.removed,
              });
            }}
            maxFiles={10}
            uploadFile={(file) =>
              uploadAttachment.mutateAsync({
                data: file,
                fileName: file.name,
              })
            }
            downloadFile={(id) => resolveAttachmentUrl.mutateAsync(id)}
          />
        </Input.Wrapper>
      </Stack>

      <CommentDrawer
        issue={selectedIssue}
        jobId={jobId}
        isOpen={selectedIssue !== null}
        onClose={() => setSelectedIssueId(null)}
      />
    </Paper>
  );
};
